import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../index.css';

const ManageHallOfFame = () => {
  const [members, setMembers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [notes, setNotes] = useState('');
  const [featured, setFeatured] = useState(false);
  const [displayOrder, setDisplayOrder] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };
      
      const [membersRes, usersRes] = await Promise.all([
        axios.get('/api/admin/hall-of-fame', config),
        axios.get('/api/admin/users', {
          params: { limit: 1000 }, // Get all users, no pagination for selection
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setMembers(membersRes.data.data || []);
      setCategories(membersRes.data.categories || {});
      setAllUsers(usersRes.data.data.users || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load data: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    
    if (!selectedUser || selectedCategories.length === 0) {
      setError('Please select a user and at least one category');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };
      
      await axios.post('/api/admin/hall-of-fame', {
        userId: selectedUser,
        categories: selectedCategories,
        notes,
        featured,
        displayOrder
      }, config);
      
      setSuccess('Member added to Hall of Fame successfully!');
      setShowAddForm(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error adding member:', error);
      setError(error.response?.data?.message || 'Failed to add member');
    }
  };

  const handleRemoveCategory = async (memberId, category) => {
    if (!window.confirm(`Remove this category from this member?`)) return;

    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };
      
      await axios.delete(`/api/admin/hall-of-fame/${memberId}/category/${category}`, config);
      setSuccess('Category removed successfully!');
      fetchData();
    } catch (error) {
      console.error('Error removing category:', error);
      setError(error.response?.data?.message || 'Failed to remove category');
    }
  };

  const handleDeleteMember = async (memberId) => {
    if (!window.confirm('Remove this member from Hall of Fame entirely?')) return;

    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };
      
      await axios.delete(`/api/admin/hall-of-fame/${memberId}`, config);
      setSuccess('Member removed from Hall of Fame!');
      fetchData();
    } catch (error) {
      console.error('Error deleting member:', error);
      setError(error.response?.data?.message || 'Failed to delete member');
    }
  };

  const handleToggleFeatured = async (memberId, currentFeatured) => {
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };
      
      const member = members.find(m => m._id === memberId);
      await axios.put(`/api/admin/hall-of-fame/${memberId}`, {
        featured: !currentFeatured,
        categories: member.categories,
        notes: member.notes,
        displayOrder: member.displayOrder
      }, config);
      fetchData();
    } catch (error) {
      console.error('Error updating featured status:', error);
      setError(error.response?.data?.message || 'Failed to update member');
    }
  };

  const resetForm = () => {
    setSelectedUser('');
    setSelectedCategories([]);
    setNotes('');
    setFeatured(false);
    setDisplayOrder(0);
  };

  const handleCategoryToggle = (category) => {
    if (selectedCategories.includes(category)) {
      setSelectedCategories(selectedCategories.filter(c => c !== category));
    } else {
      setSelectedCategories([...selectedCategories, category]);
    }
  };

  const filteredUsers = (allUsers || []).filter(user => 
    user.username.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getCategoryBadgeStyle = (categoryKey) => {
    const colors = {
      union_amplifiers: '#FF6B6B',
      giveaway_legends: '#4ECDC4',
      vision_shapers: '#FFD93D',
      growth_architects: '#95E1D3',
      union_backers_bronze: '#CD7F32',
      union_backers_silver: '#C0C0C0',
      union_backers_gold: '#FFD700',
      union_backers_platinum: '#E5E4E2'
    };
    
    return {
      backgroundColor: colors[categoryKey] || '#6C757D',
      color: ['union_backers_gold', 'vision_shapers'].includes(categoryKey) ? '#000' : '#FFF',
      padding: '4px 12px',
      borderRadius: '20px',
      fontSize: '0.85rem',
      fontWeight: 'bold',
      display: 'inline-block',
      margin: '4px'
    };
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '2rem' }}>Loading...</div>;
  }

  return (
    <div style={{ padding: '2rem', maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '2rem',
        borderRadius: '15px',
        marginBottom: '2rem',
        color: '#FFF'
      }}>
        <h1 style={{ margin: '0 0 0.5rem 0', fontSize: '2.5rem' }}>
          🏆 Hall of Fame Management
        </h1>
        <p style={{ margin: 0, opacity: 0.9 }}>
          Manage legendary members who have contributed to GamersUnion
        </p>
      </div>

      {error && (
        <div style={{
          background: '#ff4444',
          color: '#FFF',
          padding: '1rem',
          borderRadius: '8px',
          marginBottom: '1rem'
        }}>
          {error}
          <button 
            onClick={() => setError(null)}
            style={{
              float: 'right',
              background: 'transparent',
              border: 'none',
              color: '#FFF',
              cursor: 'pointer',
              fontSize: '1.2rem'
            }}
          >
            ×
          </button>
        </div>
      )}

      {success && (
        <div style={{
          background: '#44ff44',
          color: '#000',
          padding: '1rem',
          borderRadius: '8px',
          marginBottom: '1rem'
        }}>
          {success}
          <button 
            onClick={() => setSuccess(null)}
            style={{
              float: 'right',
              background: 'transparent',
              border: 'none',
              color: '#000',
              cursor: 'pointer',
              fontSize: '1.2rem'
            }}
          >
            ×
          </button>
        </div>
      )}

      <button
        onClick={() => setShowAddForm(!showAddForm)}
        style={{
          background: showAddForm ? '#dc3545' : '#28a745',
          color: '#FFF',
          border: 'none',
          padding: '12px 24px',
          borderRadius: '8px',
          cursor: 'pointer',
          fontSize: '1rem',
          fontWeight: 'bold',
          marginBottom: '2rem'
        }}
      >
        {showAddForm ? '✕ Cancel' : '+ Add Member to Hall of Fame'}
      </button>

      {showAddForm && (
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          padding: '2rem',
          borderRadius: '12px',
          marginBottom: '2rem',
          border: '2px solid rgba(255, 255, 255, 0.1)'
        }}>
          <h2 style={{ marginTop: 0 }}>Add New Member</h2>
          <form onSubmit={handleAddMember}>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                Search and Select User:
                {allUsers.length > 0 && (
                  <span style={{ marginLeft: '10px', opacity: 0.7, fontWeight: 'normal', fontSize: '0.9rem' }}>
                    ({filteredUsers.length} of {allUsers.length} users)
                  </span>
                )}
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search users..."
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '8px',
                  border: '2px solid rgba(255, 255, 255, 0.1)',
                  background: 'rgba(0, 0, 0, 0.3)',
                  color: '#FFF',
                  marginBottom: '1rem'
                }}
              />
              <select
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '8px',
                  border: '2px solid rgba(255, 255, 255, 0.1)',
                  background: 'rgba(0, 0, 0, 0.3)',
                  color: '#FFF'
                }}
                required
              >
                <option value="" style={{ background: '#1a1a1a', color: '#FFF' }}>
                  -- Select User --
                </option>
                {filteredUsers.map(user => (
                  <option 
                    key={user._id} 
                    value={user._id}
                    style={{ background: '#1a1a1a', color: '#FFF' }}
                  >
                    {user.username} ({user.email})
                  </option>
                ))}
              </select>
              {filteredUsers.length === 0 && searchTerm && (
                <div style={{ 
                  marginTop: '0.5rem', 
                  color: '#FFA500', 
                  fontSize: '0.9rem' 
                }}>
                  No users found matching "{searchTerm}"
                </div>
              )}
              {allUsers.length === 0 && !loading && (
                <div style={{ 
                  marginTop: '0.5rem', 
                  color: '#FF6B6B', 
                  fontSize: '0.9rem' 
                }}>
                  No users available. Please check your connection.
                </div>
              )}
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                Select Categories:
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '10px' }}>
                {Object.entries(categories).map(([key, cat]) => (
                  <label 
                    key={key}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '10px',
                      background: selectedCategories.includes(key) 
                        ? 'rgba(100, 200, 100, 0.2)' 
                        : 'rgba(255, 255, 255, 0.05)',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      border: selectedCategories.includes(key) 
                        ? '2px solid #28a745' 
                        : '2px solid rgba(255, 255, 255, 0.1)'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedCategories.includes(key)}
                      onChange={() => handleCategoryToggle(key)}
                      style={{ marginRight: '10px' }}
                    />
                    <div>
                      <div style={{ fontWeight: 'bold' }}>
                        {cat.icon} {cat.name}
                      </div>
                      <div style={{ fontSize: '0.85rem', opacity: 0.7 }}>
                        {cat.description}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                Notes (Optional):
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any special notes about this member..."
                rows="3"
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '8px',
                  border: '2px solid rgba(255, 255, 255, 0.1)',
                  background: 'rgba(0, 0, 0, 0.3)',
                  color: '#FFF',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '2rem', marginBottom: '1.5rem' }}>
              <label style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={featured}
                  onChange={(e) => setFeatured(e.target.checked)}
                  style={{ marginRight: '10px' }}
                />
                <span style={{ fontWeight: 'bold' }}>⭐ Featured</span>
              </label>

              <label style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                <span style={{ marginRight: '10px', fontWeight: 'bold' }}>Display Order:</span>
                <input
                  type="number"
                  value={displayOrder}
                  onChange={(e) => setDisplayOrder(parseInt(e.target.value) || 0)}
                  style={{
                    width: '100px',
                    padding: '8px',
                    borderRadius: '8px',
                    border: '2px solid rgba(255, 255, 255, 0.1)',
                    background: 'rgba(0, 0, 0, 0.3)',
                    color: '#FFF'
                  }}
                />
              </label>
            </div>

            <button
              type="submit"
              style={{
                background: '#28a745',
                color: '#FFF',
                border: 'none',
                padding: '12px 32px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 'bold'
              }}
            >
              Add to Hall of Fame
            </button>
          </form>
        </div>
      )}

      <h2 style={{ marginBottom: '1rem' }}>Current Members ({members.length})</h2>
      
      {members.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '3rem',
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '12px'
        }}>
          <p style={{ fontSize: '1.2rem', opacity: 0.7 }}>
            No members in Hall of Fame yet. Add the first legend!
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '1rem' }}>
          {members.map(member => (
            <div
              key={member._id}
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                padding: '1.5rem',
                borderRadius: '12px',
                border: member.featured ? '3px solid #FFD700' : '2px solid rgba(255, 255, 255, 0.1)',
                position: 'relative'
              }}
            >
              {member.featured && (
                <div style={{
                  position: 'absolute',
                  top: '-12px',
                  left: '20px',
                  background: '#FFD700',
                  color: '#000',
                  padding: '4px 12px',
                  borderRadius: '20px',
                  fontSize: '0.85rem',
                  fontWeight: 'bold'
                }}>
                  ⭐ FEATURED
                </div>
              )}
              
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.5rem' }}>
                    {member.userId?.username || 'Unknown User'}
                  </h3>
                  
                  <div style={{ marginBottom: '1rem' }}>
                    {member.categories.map(cat => (
                      <span key={cat} style={{ position: 'relative', display: 'inline-block' }}>
                        <span style={getCategoryBadgeStyle(cat)}>
                          {categories[cat]?.icon} {categories[cat]?.name}
                        </span>
                        <button
                          onClick={() => handleRemoveCategory(member._id, cat)}
                          style={{
                            position: 'absolute',
                            top: '-5px',
                            right: '-5px',
                            background: '#ff4444',
                            color: '#FFF',
                            border: 'none',
                            borderRadius: '50%',
                            width: '20px',
                            height: '20px',
                            cursor: 'pointer',
                            fontSize: '0.8rem',
                            padding: 0
                          }}
                          title="Remove category"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>

                  {member.notes && (
                    <p style={{ 
                      margin: '0.5rem 0',
                      padding: '10px',
                      background: 'rgba(0, 0, 0, 0.2)',
                      borderRadius: '8px',
                      fontSize: '0.9rem'
                    }}>
                      📝 {member.notes}
                    </p>
                  )}

                  <div style={{ fontSize: '0.85rem', opacity: 0.7, marginTop: '0.5rem' }}>
                    Display Order: {member.displayOrder} | Added: {new Date(member.createdAt).toLocaleDateString()}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '10px', marginLeft: '1rem' }}>
                  <button
                    onClick={() => handleToggleFeatured(member._id, member.featured)}
                    style={{
                      background: member.featured ? '#FFA500' : '#6c757d',
                      color: '#FFF',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '0.9rem'
                    }}
                    title={member.featured ? 'Unfeature' : 'Feature'}
                  >
                    {member.featured ? '⭐' : '☆'}
                  </button>
                  <button
                    onClick={() => handleDeleteMember(member._id)}
                    style={{
                      background: '#dc3545',
                      color: '#FFF',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '0.9rem'
                    }}
                  >
                    🗑️ Remove
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ManageHallOfFame;
